import { http } from "@/lib/http";
import type { PrecheckReport } from "@/lib/prechecks";

import type {
  AvailabilityZone,
  PaginatedVolumes,
  Volume,
  VolumeAttachRequest,
  VolumeBackup,
  VolumeBackupCreateRequest,
  VolumeBackupRestoreRequest,
  VolumeBackupRestoreResult,
  VolumeCreateRequest,
  VolumeCreateResponse,
  VolumeImageUploadResult,
  VolumeMetadata,
  VolumeRetypeRequest,
  VolumeSnapshot,
  VolumeSnapshotCreateRequest,
  VolumeSnapshotUpdateRequest,
  VolumeType,
  VolumeUpdateRequest,
  VolumeUploadToImageRequest,
} from "./types";

export async function listVolumeTypes(): Promise<VolumeType[]> {
  const { data } = await http.get<VolumeType[]>("/storage/volume-types");
  return data;
}

export async function listAvailabilityZones(): Promise<AvailabilityZone[]> {
  const { data } = await http.get<AvailabilityZone[]>(
    "/storage/availability-zones",
  );
  return data;
}

export interface VolumeListFilters {
  /** Case-insensitive substring match on the volume name. */
  name?: string;
  /** Case-insensitive exact status match (e.g. "available"). */
  status?: string;
}

export async function listVolumes(params: {
  page: number;
  pageSize: number;
  filters?: VolumeListFilters;
}): Promise<PaginatedVolumes> {
  const { data } = await http.get<PaginatedVolumes>("/storage/volumes", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      // Filtered server-side before pagination; unknown params are ignored
      // until the backend support lands, so this degrades gracefully.
      name: params.filters?.name || undefined,
      status: params.filters?.status || undefined,
    },
  });
  return data;
}

export async function getVolume(volumeId: string): Promise<Volume> {
  const { data } = await http.get<Volume>(`/storage/volumes/${volumeId}`);
  return data;
}

/** Dry run of a create: every prerequisite and what to fix. Creates nothing. */
export async function precheckVolume(payload: VolumeCreateRequest): Promise<PrecheckReport> {
  const { data } = await http.post<PrecheckReport>("/storage/volumes/precheck", payload);
  return data;
}

export async function createVolume(
  payload: VolumeCreateRequest,
): Promise<VolumeCreateResponse> {
  const { data } = await http.post<VolumeCreateResponse>(
    "/storage/volumes",
    payload,
  );
  return data;
}

export async function updateVolume(
  volumeId: string,
  payload: VolumeUpdateRequest,
): Promise<Volume> {
  const { data } = await http.put<Volume>(
    `/storage/volumes/${volumeId}`,
    payload,
  );
  return data;
}

export async function deleteVolume(volumeId: string): Promise<void> {
  await http.delete(`/storage/volumes/${volumeId}`);
}

export async function extendVolume(
  volumeId: string,
  newSizeGb: number,
): Promise<Volume> {
  const { data } = await http.post<Volume>(
    `/storage/volumes/${volumeId}/extend`,
    { new_size_gb: newSizeGb },
  );
  return data;
}

export async function retypeVolume(
  volumeId: string,
  payload: VolumeRetypeRequest,
): Promise<Volume> {
  const { data } = await http.post<Volume>(
    `/storage/volumes/${volumeId}/retype`,
    payload,
  );
  return data;
}

export async function attachVolume(
  volumeId: string,
  payload: VolumeAttachRequest,
): Promise<void> {
  await http.post(`/storage/volumes/${volumeId}/attach`, payload);
}

export async function detachVolume(
  volumeId: string,
  serverId: string,
): Promise<void> {
  // server_id goes in the JSON body (symmetric with attach). Sending it as a
  // query param left the body empty -> backend 422 "field required".
  await http.post(`/storage/volumes/${volumeId}/detach`, {
    server_id: serverId,
  });
}

export async function revertVolumeToSnapshot(
  volumeId: string,
  snapshotId: string,
): Promise<Volume> {
  const { data } = await http.post<Volume>(
    `/storage/volumes/${volumeId}/revert-to-snapshot`,
    { snapshot_id: snapshotId },
  );
  return data;
}

export async function uploadVolumeToImage(
  volumeId: string,
  payload: VolumeUploadToImageRequest,
): Promise<VolumeImageUploadResult> {
  const { data } = await http.post<VolumeImageUploadResult>(
    `/storage/volumes/${volumeId}/upload-to-image`,
    payload,
  );
  return data;
}

export async function getVolumeMetadata(
  volumeId: string,
): Promise<VolumeMetadata> {
  const { data } = await http.get<VolumeMetadata>(
    `/storage/volumes/${volumeId}/metadata`,
  );
  return data;
}

export async function setVolumeMetadata(
  volumeId: string,
  metadata: Record<string, string>,
): Promise<VolumeMetadata> {
  const { data } = await http.put<VolumeMetadata>(
    `/storage/volumes/${volumeId}/metadata`,
    { metadata },
  );
  return data;
}

export async function getSnapshot(snapshotId: string): Promise<VolumeSnapshot> {
  const { data } = await http.get<VolumeSnapshot>(
    `/storage/snapshots/${snapshotId}`,
  );
  return data;
}

export async function listSnapshots(
  volumeId?: string,
): Promise<VolumeSnapshot[]> {
  const { data } = await http.get<VolumeSnapshot[]>("/storage/snapshots", {
    params: volumeId ? { volume_id: volumeId } : undefined,
  });
  return data;
}

export async function createSnapshot(
  payload: VolumeSnapshotCreateRequest,
): Promise<VolumeSnapshot> {
  const { data } = await http.post<VolumeSnapshot>(
    "/storage/snapshots",
    payload,
  );
  return data;
}

export async function updateSnapshot(
  snapshotId: string,
  payload: VolumeSnapshotUpdateRequest,
): Promise<VolumeSnapshot> {
  const { data } = await http.put<VolumeSnapshot>(
    `/storage/snapshots/${snapshotId}`,
    payload,
  );
  return data;
}

export async function deleteSnapshot(snapshotId: string): Promise<void> {
  await http.delete(`/storage/snapshots/${snapshotId}`);
}

export async function listBackups(volumeId?: string): Promise<VolumeBackup[]> {
  const { data } = await http.get<VolumeBackup[]>("/storage/backups", {
    params: volumeId ? { volume_id: volumeId } : undefined,
  });
  return data;
}

export async function createBackup(
  payload: VolumeBackupCreateRequest,
): Promise<VolumeBackup> {
  const { data } = await http.post<VolumeBackup>("/storage/backups", payload);
  return data;
}

export async function restoreBackup(
  backupId: string,
  payload: VolumeBackupRestoreRequest,
): Promise<VolumeBackupRestoreResult> {
  const { data } = await http.post<VolumeBackupRestoreResult>(
    `/storage/backups/${backupId}/restore`,
    payload,
  );
  return data;
}

export async function deleteBackup(
  backupId: string,
  force = false,
): Promise<void> {
  await http.delete(`/storage/backups/${backupId}`, {
    params: force ? { force: true } : undefined,
  });
}
